import {paraphraseRequest} from '@apis/paraphrase'
import {Box, Flex, Text, Tooltip} from '@chakra-ui/react'
import Loading from '@components/Loading'
import {
  updateAudio,
  updateDragDropDataLength,
  updateParaphraseData,
} from '@redux/paraphrase/paraphraseSlice'
import {CommonColors} from '@utils/CommonStyles'
import {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {updateIsShowFooter} from '@redux/paraphrase/paraphraseSlice'
import RightAudio from '@assets/audios/right.mp3'
import WrongAudio from '@assets/audios/wrong.mp3'

const DragDrop = () => {
  const {sessionId, colorsParaphrase, currentIndexDragDropData} = useSelector(
    (state: any) => state.paraphrase,
  )
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [dragDropData, setDragDropData] = useState<any>([])
  const [droppedItems, setDroppedItems] = useState<{[key: string]: string[]}>(
    {},
  )
  const [draggedWords, setDraggedWords] = useState<string[]>([])
  const [feedbackItems, setFeedbackItems] = useState<{
    [key: string]: {text: string; isCorrect: boolean | null}[]
  }>({})
  const [isCompleted, setIsCompleted] = useState<boolean>(false)
  const dispatch = useDispatch()

  useEffect(() => {
    const audioData = {
      time: Date.now(),
      url: 'https://smedia.stepup.edu.vn/ielts/audio/paraphrase/keo_tu.wav',
    }
    dispatch(updateAudio(audioData))
  }, [])

  useEffect(() => {
    const getParaphraseData = async () => {
      try {
        const res = await paraphraseRequest.getParaphraseData({sessionId})

        // Randomize synonyms array before processing
        const randomizedData = {
          ...res?.data?.data,
          synonyms: [...res?.data?.data?.synonyms].sort(
            () => Math.random() - 0.5,
          ),
        }

        dispatch(updateParaphraseData(randomizedData))
        const words = randomizedData.words
        const synonyms = randomizedData.synonyms
        let updateDragDropData: any[] = []

        if (words.length <= 3) {
          updateDragDropData = [
            {
              words: words,
              synonyms: synonyms.filter((syn: any) => words.includes(syn.word)),
            },
          ]
        } else if (words.length === 4) {
          updateDragDropData = [
            {
              words: [words[0], words[1]],
              synonyms: synonyms.filter((syn: any) =>
                [words[0], words[1]].includes(syn.word),
              ),
            },
            {
              words: [words[2], words[3]],
              synonyms: synonyms.filter((syn: any) =>
                [words[2], words[3]].includes(syn.word),
              ),
            },
          ]
        } else if (words.length === 5) {
          updateDragDropData = [
            {
              words: [words[0], words[1], words[2]],
              synonyms: synonyms.filter((syn: any) =>
                [words[0], words[1], words[2]].includes(syn.word),
              ),
            },
            {
              words: [words[3], words[4]],
              synonyms: synonyms.filter((syn: any) =>
                [words[3], words[4]].includes(syn.word),
              ),
            },
          ]
        }

        setDragDropData(updateDragDropData)
        dispatch(updateDragDropDataLength(updateDragDropData.length))
      } catch (error) {
      } finally {
        setIsLoading(false)
      }
    }
    getParaphraseData()
  }, [sessionId])

  // Add new useEffect to reset states when currentIndexDragDropData changes
  useEffect(() => {
    setDroppedItems({})
    setDraggedWords([])
    setFeedbackItems({})
    setIsCompleted(false)
    dispatch(updateIsShowFooter(false))
  }, [currentIndexDragDropData])

  const handlePlayAudio = (audioUrl: string) => {
    const audioData = {
      time: Date.now(),
      url: audioUrl,
    }
    dispatch(updateAudio(audioData))
  }

  const handleDragStart = (e: React.DragEvent, word: string) => {
    e.dataTransfer.setData('text', word)
  }

  const handleDrop = (e: React.DragEvent, targetWord: string) => {
    e.preventDefault()
    const droppedWord = e.dataTransfer.getData('text')

    const isValidSynonym = dragDropData[
      currentIndexDragDropData
    ]?.synonyms.some(
      (syn: any) => syn.word === targetWord && syn.text === droppedWord,
    )

    // Add the word to draggedWords immediately, regardless of correctness
    setDraggedWords(prev => [...prev, droppedWord])

    // Add the word to droppedItems regardless of correctness
    setDroppedItems(prev => ({
      ...prev,
      [targetWord]: [...(prev[targetWord] || []), droppedWord],
    }))

    // Update feedback state
    setFeedbackItems(prev => ({
      ...prev,
      [targetWord]: [
        ...(prev[targetWord] || []),
        {text: droppedWord, isCorrect: isValidSynonym},
      ],
    }))

    if (isValidSynonym) {
      handlePlayAudio(RightAudio)
    } else {
      handlePlayAudio(WrongAudio)
    }

    // Kiểm tra xem đã hoàn thành chưa sau khi thêm từ mới
    setTimeout(() => {
      if (isValidSynonym) {
        const allWords = dragDropData[currentIndexDragDropData]?.words || []
        const isAllCompleted =
          dragDropData[currentIndexDragDropData]?.synonyms?.length ===
          [...draggedWords, droppedWord].length
        setIsCompleted(isAllCompleted)
        if (isAllCompleted) {
          dispatch(updateIsShowFooter(true))
        }
      }

      if (!isValidSynonym) {
        // Remove incorrect word from droppedItems
        setDroppedItems(prev => ({
          ...prev,
          [targetWord]: prev[targetWord].filter(item => item !== droppedWord),
        }))
        // Remove feedback for incorrect word
        setFeedbackItems(prev => ({
          ...prev,
          [targetWord]: prev[targetWord].filter(
            item => item.text !== droppedWord,
          ),
        }))
        // Re-enable the word if it was incorrect
        setDraggedWords(prev => prev.filter(word => word !== droppedWord))
      } else {
        // Just reset feedback state for correct words
        setFeedbackItems(prev => ({
          ...prev,
          [targetWord]: prev[targetWord].map(item => ({
            ...item,
            isCorrect: null,
          })),
        }))
      }
    }, 1000)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
  }

  return (
    <Box
      flexDirection={'column'}
      bg={'#fff'}
      p={{base: '16px', md: '32px'}}
      borderRadius={'24px'}
      h={'100%'}
      position={'relative'}
      overflow={'auto'}>
      {isLoading && <Loading />}
      <Text
        fontSize={{base: '16px', md: '18px'}}
        textAlign={'center'}
        textTransform={'uppercase'}
        color={CommonColors.primary}>
        Kéo từ vào ô đúng với nghĩa của chúng
      </Text>

      {isCompleted ? (
        <Text
          textAlign="center"
          fontSize={{base: '20px', md: '24px'}}
          mt={{base: '16px', md: '32px'}}
          fontWeight="500">
          Từ càng lớn, nghĩa càng gần bạn nhé!
        </Text>
      ) : (
        <Flex
          mt={{base: '16px', md: '32px'}}
          gap={{base: '8px', md: '16px'}}
          justifyContent={'center'}
          flexWrap={'wrap'}>
          {dragDropData[currentIndexDragDropData]?.synonyms.map(
            (word: any, index: number) => (
              <Tooltip
                key={index}
                label={
                  dragDropData[currentIndexDragDropData]?.synonyms.find(
                    (syn: any) => syn.text === word.text,
                  )?.mean_vnese
                }
                hasArrow
                placement="top">
                <Text
                  draggable={!draggedWords.includes(word.text)}
                  onDragStart={e => handleDragStart(e, word.text)}
                  fontSize={'16px'}
                  fontWeight={'500'}
                  border={'1px solid #D0D5DD'}
                  borderRadius={'8px'}
                  p={'4px 8px'}
                  cursor={
                    draggedWords.includes(word.text) ? 'not-allowed' : 'grab'
                  }
                  opacity={draggedWords.includes(word.text) ? 0.5 : 1}
                  whiteSpace={'nowrap'}>
                  {word.text}
                </Text>
              </Tooltip>
            ),
          )}
        </Flex>
      )}

      <Flex
        mt={{base: '16px', md: '32px'}}
        gap={{base: '16px', md: '32px'}}
        justifyContent={'center'}
        flexDirection={{base: 'column', md: 'row'}}
        alignItems={'center'}>
        {dragDropData[currentIndexDragDropData]?.words.map(
          (word: any, index: number) => (
            <Flex
              key={index}
              flexDirection={'column'}
              alignItems={'center'}
              w={{base: '100%', md: 'auto'}}
              minW={{base: 'auto', md: '300px'}}
              minH={{base: '250px', md: '350px'}}
              borderRadius={'24px'}
              border={'1px solid #8ED4FF'}
              p={{base: '12px', md: '16px'}}
              boxShadow={'0px 8px 0px 0px #8ED4FF'}
              onDrop={e => handleDrop(e, word)}
              onDragOver={handleDragOver}>
              <Text
                mb={{base: '12px', md: '16px'}}
                fontSize={{base: '18px', md: '20px'}}
                fontWeight={'500'}
                color={colorsParaphrase[index].color}
                bg={colorsParaphrase[index].bg}
                borderRadius={'8px'}
                border={`1px solid ${colorsParaphrase[index].color}`}
                p={'4px 8px'}>
                {word}
              </Text>
              {droppedItems[word] &&
                [...droppedItems[word]]
                  .sort((a, b) => {
                    const synonymA = dragDropData[
                      currentIndexDragDropData
                    ]?.synonyms.find(
                      (syn: any) => syn.text === a && syn.word === word,
                    )
                    const synonymB = dragDropData[
                      currentIndexDragDropData
                    ]?.synonyms.find(
                      (syn: any) => syn.text === b && syn.word === word,
                    )
                    return (synonymA?.weight || 3) - (synonymB?.weight || 3)
                  })
                  .map((item, idx) => {
                    const synonym = dragDropData[
                      currentIndexDragDropData
                    ]?.synonyms.find(
                      (syn: any) => syn.text === item && syn.word === word,
                    )
                    const fontSize =
                      synonym?.weight === 1
                        ? '20px'
                        : synonym?.weight === 2
                          ? '18px'
                          : '16px'

                    // Find feedback by matching the item text instead of using index
                    const feedback = feedbackItems[word]?.find(
                      f => f.text === item,
                    )

                    return (
                      <Text
                        key={idx}
                        mt={'16px'}
                        fontSize={fontSize}
                        fontWeight={'500'}
                        border={
                          feedback?.isCorrect === true
                            ? '1px solid #47CD89'
                            : feedback?.isCorrect === false
                              ? '1px solid #E74C3C'
                              : '1px solid #D0D5DD'
                        }
                        color={
                          feedback?.isCorrect === true
                            ? '#47CD89'
                            : feedback?.isCorrect === false
                              ? '#E74C3C'
                              : 'inherit'
                        }
                        borderRadius={'8px'}
                        p={'4px 8px'}>
                        {item}
                      </Text>
                    )
                  })}
            </Flex>
          ),
        )}
      </Flex>
    </Box>
  )
}

export default DragDrop
